import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { TileEmpty } from '../TileWrapper';
import { CheckSquare, Circle, CircleDot } from 'lucide-react';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = [
  { id: '1', title: 'Complete safety training review', status: 'in_progress', due_date: new Date().toISOString().split('T')[0], priority: 'high', project_id: '1', projects: { name: 'Site Alpha' } },
  { id: '2', title: 'Update COSHH assessments', status: 'todo', due_date: new Date(Date.now() + 86400000).toISOString().split('T')[0], priority: 'urgent', project_id: '1', projects: { name: 'Main Office' } },
  { id: '3', title: 'Schedule fire drill', status: 'todo', due_date: new Date(Date.now() + 172800000).toISOString().split('T')[0], priority: null, project_id: '2', projects: { name: 'Warehouse B' } },
  { id: '4', title: 'Review contractor permits', status: 'in_progress', due_date: new Date(Date.now() - 86400000).toISOString().split('T')[0], priority: 'high', project_id: '1', projects: { name: 'Site Alpha' } },
  { id: '5', title: 'Update PPE inventory', status: 'todo', due_date: new Date(Date.now() + 345600000).toISOString().split('T')[0], priority: null, project_id: '3', projects: { name: 'Distribution Center' } },
];

export function MyTasksTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(size === 'large' ? MOCK_DATA : MOCK_DATA.slice(0, size === 'tall' ? 4 : 3));
      setLoading(false);
      return;
    }

    if (!userProfile?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('tasks')
          .select(`
            id,
            title,
            status,
            due_date,
            priority,
            project_id,
            projects(name)
          `)
          .contains('assigned_to', [userProfile.id])
          .neq('status', 'done')
          .order('due_date', { ascending: true, nullsFirst: false })
          .limit(size === 'large' ? 10 : size === 'tall' ? 8 : 5);

        if (filter?.spaceId) {
          query = query.eq('project_id', filter.spaceId);
        }

        const { data: tasks, error } = await query;

        if (error) throw error;

        setData(tasks || []);
      } catch (err) {
        console.error('Failed to load tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.id, filter?.spaceId, size, useMockData]);

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isPast(date)) return `${Math.ceil((new Date() - date) / (1000 * 60 * 60 * 24))}d overdue`;
    return format(date, 'MMM d');
  };

  const getDueDateColor = (dateStr) => {
    if (!dateStr) return 'secondary';
    const date = new Date(dateStr);
    if (isPast(date) && !isToday(date)) return 'destructive';
    if (isToday(date)) return 'warning';
    return 'secondary';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_progress':
        return <CircleDot className="h-4 w-4 text-blue-500" />;
      case 'done':
        return <CheckSquare className="h-4 w-4 text-green-500" />;
      default:
        return <Circle className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="w-4 h-4 rounded bg-gray-200" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return <TileEmpty message="No tasks assigned" icon={CheckSquare} />;
  }

  return (
    <div className="space-y-2">
      {data.map((task) => (
        <div
          key={task.id}
          onClick={() => navigate(`/space/${task.project_id}/tasks`)}
          className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
        >
          {getStatusIcon(task.status)}

          <div className="flex-1 min-w-0">
            <p className={cn(
              "text-sm font-medium truncate",
              task.status === 'done' && "line-through text-gray-400"
            )}>
              {task.title}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 truncate">
                {task.projects?.name}
              </span>
              {task.priority && (
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs px-1 py-0 h-4",
                    task.priority === 'urgent' && "border-red-500 text-red-600",
                    task.priority === 'high' && "border-orange-500 text-orange-600"
                  )}
                >
                  {task.priority}
                </Badge>
              )}
            </div>
          </div>

          {task.due_date && (
            <Badge variant={getDueDateColor(task.due_date)} className="shrink-0 text-xs">
              {formatDueDate(task.due_date)}
            </Badge>
          )}
        </div>
      ))}

      <button
        onClick={() => navigate('/spaces')}
        className="w-full text-center text-sm text-blue-600 hover:text-blue-700 py-2"
      >
        View All Tasks
      </button>
    </div>
  );
}
